import React, { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { useApiErrors, useDomainEvent, useEvent, useLazyCommand } from '@soap/modules';
import { ThinkingAnimation } from '../ThinkingAnimation';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { lessonLoadingStateKey, normalizeIdPart } from '../InProgressUnitsPanel/loadingStateKeys';
import type { LessonDetailDto } from '../InProgressUnitsPanel/types';

interface CustomiseLessonDialogProps {
    unitId: string;
    lessonId: string;
    lesson: LessonDetailDto;
    onClose: () => void;
    onSuccess?: () => void;
    setLoadingStates?: React.Dispatch<React.SetStateAction<Set<string>>>;
    onRevertOptimistic?: () => void;
}

const GUIDANCE_EXAMPLES = [
    'We don\'t have markers, using crayons instead',
    'Sore thumb today, can\'t cut any paper',
    'It\'s raining now, we\'ll need to re-imagine for indoors',
    'Please replace step 2 with ...',
    'It\'s too easy, can we make it more challenging?'
] as const;

const GUIDANCE_PLACEHOLDER = `Examples:\n${GUIDANCE_EXAMPLES.map(example => `• ${example}`).join('\n')}`;

const viewStates = {
    ready: 0,
    submitting: 1
};

export function CustomiseLessonDialog({
    unitId,
    lessonId,
    lesson,
    onClose,
    onSuccess,
    setLoadingStates,
    onRevertOptimistic
}: CustomiseLessonDialogProps) {
    const [guidance, setGuidance] = useState('');
    const [viewState, setViewState] = useState(viewStates.ready);
    const awaitingE326Ref = React.useRef(false);

    const [sendCustomiseCommand] = useLazyCommand('C326v1_CustomiseLessonAtDelivery');
    const triggerCloseContextMenu = useDomainEvent({ eventName: 'close-context-menu' });

    const lessonLoadingKey = lessonLoadingStateKey(unitId, lessonId);
    const clearLessonLoading = useCallback(() => {
        setLoadingStates?.(prev => {
            const next = new Set(prev);
            next.delete(lessonLoadingKey);
            return next;
        });
    }, [lessonLoadingKey, setLoadingStates]);

    useApiErrors(() => {
        if (viewState === viewStates.submitting) {
            setViewState(viewStates.ready);
        }
        awaitingE326Ref.current = false;
        clearLessonLoading();
        onRevertOptimistic?.();
    });

    useEvent({
        eventName: 'E326v1_LessonCustomisedAtDelivery',
        onEventReceived: event => {
            const eAny = event as Record<string, unknown>;
            const eLesson = eAny.e326_LessonId ?? eAny.E326_LessonId;
            const eUnit = eAny.e326_UnitId ?? eAny.E326_UnitId;
            const sameLesson = normalizeIdPart(eLesson) === normalizeIdPart(lessonId);
            const sameUnit = normalizeIdPart(eUnit) === normalizeIdPart(unitId);
            if (!awaitingE326Ref.current || !sameLesson || !sameUnit) return;

            awaitingE326Ref.current = false;
            setViewState(viewStates.ready);
            clearLessonLoading();
            setGuidance('');
            if (onSuccess) {
                onSuccess();
            } else {
                onClose();
            }
            triggerCloseContextMenu();
        }
    });

    const handleClose = useCallback(() => {
        awaitingE326Ref.current = false;
        setViewState(viewStates.ready);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = guidance.trim();
        if (!trimmed) return;

        setLoadingStates?.(prev => new Set(prev).add(lessonLoadingKey));
        awaitingE326Ref.current = true;
        setViewState(viewStates.submitting);

        await new Promise(resolve => requestAnimationFrame(resolve));

        try {
            await sendCustomiseCommand({
                C326_UnitId: unitId,
                C326_LessonId: lessonId,
                C326_Guidance: trimmed
            });
        } catch (error) {
            console.error('Error customising lesson:', error);
            awaitingE326Ref.current = false;
            setViewState(viewStates.ready);
            clearLessonLoading();
            onRevertOptimistic?.();
        }
    };

    return (
        <Dialog open onOpenChange={open => !open && handleClose()}>
            <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
                {viewState === viewStates.submitting ? (
                    <div className="flex flex-col items-center justify-center px-6 py-12">
                        <ThinkingAnimation />
                        <p className="mt-4 text-sm text-gray-600 text-center">
                            Customising {lesson.e251_LessonTitle || 'lesson'}…
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                            <h2 className="text-lg font-medium text-gray-900">Customise lesson</h2>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-4 py-4 space-y-3">
                            <p className="text-sm text-gray-600">
                                Describe how you would like AI to change the lesson. Only incomplete
                                steps will be affected. Supporting materials may be updated but you
                                will be notified.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="customise-guidance">Guidance</Label>
                                <Textarea
                                    id="customise-guidance"
                                    value={guidance}
                                    onChange={e => setGuidance(e.target.value)}
                                    placeholder={GUIDANCE_PLACEHOLDER}
                                    rows={6}
                                    className="resize-y"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
                            <Button type="button" variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!guidance.trim()}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                Customise with AI
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
